import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import type { MappedGL } from 'api/types/loanMapping.types';
import type { AuditTrailEntry } from 'api/types/auditTrail.types';
import { useEnumsByGroup } from 'hooks/api';
import { ModalLoadingSkeleton } from 'components/common';
import { loanMappingService } from 'api/services/loanMappingService';
import { auditTrailService } from 'api/services/auditTrailService';

// ============================================================================
// Types
// ============================================================================

export interface LoanMappingRecord {
  id: string;
  requestId: string;
  addonCustomerId: string;
  customerName: string;
  customerId: string;
  approvedAmount: number;
  goldRateAtApproval: number;
  grossValueAtApproval: number;
  eligibleAmountAtApproval: number;
  outstandingAmount: number;
  totalNetWeight: number;
  totalGoldLoanAmount: number;
  glCount: number;
  businessUnitName?: string;
  approvedBy: string;
  approvedByName?: string;
  approvedAt: string;
  statusCode?: string;
  statusName?: string;
  createdAt: string;
  // Detail fields (populated from getById)
  addonLoanAccountNumber?: string;
  addonLoanType?: string;
  mappedGls?: MappedGL[];
}

interface LoanMappingViewModalProps {
  isOpen: boolean;
  record: LoanMappingRecord | null;
  loading?: boolean;
  onClose: () => void;
  onRecordUpdated?: (updated: LoanMappingRecord) => void;
}

// ============================================================================
// Inline Amend Form
// ============================================================================

const AmendForm: React.FC<{
  currentValue: string;
  label: string;
  onConfirm: (newNumber: string, reason: string) => void;
  onCancel: () => void;
  submitting: boolean;
}> = ({ currentValue, label, onConfirm, onCancel, submitting }) => {
  const [newNumber, setNewNumber] = useState('');
  const [reason, setReason] = useState('');

  const numberValid = newNumber.length === 14;
  const reasonValid = reason.trim().length >= 5;
  const canSubmit = numberValid && reasonValid && !submitting;

  return (
    <div style={{
      marginTop: '0.5rem',
      padding: '0.75rem',
      background: 'var(--bg-tertiary, #f8f9fb)',
      borderRadius: '0.375rem',
      border: '1px solid var(--border-color)',
    }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--primary)' }}>
        Amend {label}
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
        Current: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{currentValue || '(empty)'}</span>
      </div>
      <div style={{ marginBottom: '0.5rem' }}>
        <input
          type="text"
          maxLength={14}
          value={newNumber}
          onChange={e => setNewNumber(e.target.value)}
          placeholder="Enter new 14-char number"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '0.375rem 0.5rem',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            border: `1px solid ${newNumber && !numberValid ? '#dc2626' : 'var(--border-color)'}`,
            borderRadius: '0.25rem',
            background: 'var(--bg-primary, #fff)',
          }}
        />
        {newNumber && !numberValid && (
          <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem' }}>
            Must be exactly 14 characters ({newNumber.length}/14)
          </div>
        )}
      </div>
      <div style={{ marginBottom: '0.5rem' }}>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason for amendment (min 5 characters)"
          maxLength={500}
          disabled={submitting}
          rows={2}
          style={{
            width: '100%',
            padding: '0.375rem 0.5rem',
            fontSize: '0.8rem',
            border: `1px solid ${reason && !reasonValid ? '#dc2626' : 'var(--border-color)'}`,
            borderRadius: '0.25rem',
            resize: 'vertical',
            background: 'var(--bg-primary, #fff)',
          }}
        />
        {reason && !reasonValid && (
          <div style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.2rem' }}>
            Reason must be at least 5 characters
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={onCancel} disabled={submitting} style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={() => onConfirm(newNumber, reason)} disabled={!canSubmit} style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}>
          {submitting ? 'Saving...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// View Modal — Glassmorphism Design
// ============================================================================

export const LoanMappingViewModal: React.FC<LoanMappingViewModalProps> = ({
  isOpen,
  record,
  loading,
  onClose,
  onRecordUpdated,
}) => {
  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');
  const loanTypeLookup = useMemo(() => {
    const map: Record<string, string> = {};
    loanTypeEnums.forEach(e => { map[e.enumKey] = e.enumValue; });
    return map;
  }, [loanTypeEnums]);

  // Amend state
  const [amendingAccount, setAmendingAccount] = useState(false);
  const [amendingGlId, setAmendingGlId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [amendMessage, setAmendMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Change history state
  const [auditHistory, setAuditHistory] = useState<AuditTrailEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditExpanded, setAuditExpanded] = useState(false);

  // Fetch audit trail when modal opens or record changes
  useEffect(() => {
    if (!isOpen || !record?.id) {
      setAuditHistory([]);
      return;
    }
    let cancelled = false;
    const fetchAudit = async () => {
      setAuditLoading(true);
      try {
        const res = await auditTrailService.getByRecord('loan_mapping', record.id);
        if (!cancelled && res.data) setAuditHistory(res.data);
      } catch {
        // silently ignore — not critical
      } finally {
        if (!cancelled) setAuditLoading(false);
      }
    };
    fetchAudit();
    return () => { cancelled = true; };
  }, [isOpen, record?.id]);

  // Admin role check
  const userRole = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role || ''; }
    catch { return ''; }
  }, []);
  const isAdmin = userRole === 'lmms_system' || userRole === 'lmms_admin';

  if (!isOpen || !record) return null;

  const mappedGls = record.mappedGls ?? [];
  const totalWeight = mappedGls.reduce((s, gl) => s + (gl.netWeight ?? 0), 0);
  const status = record.statusCode ?? '';
  const isAmendable = status === 'ACTIVE' || status === 'PARTIALLY_CLOSED';

  const handleAmendAccount = async (newNumber: string, reason: string) => {
    setSubmitting(true);
    setAmendMessage(null);
    try {
      const res = await loanMappingService.amendAccountNumber(record.id, { newNumber, reason });
      const updated = res.data;
      if (updated && onRecordUpdated) {
        onRecordUpdated({
          ...record,
          addonLoanAccountNumber: updated.addonLoanAccountNumber,
          mappedGls: updated.mappedGls,
        } as LoanMappingRecord);
      }
      setAmendingAccount(false);
      setAmendMessage({ text: 'Loan Account Number amended successfully', type: 'success' });
      // Re-fetch audit trail
      auditTrailService.getByRecord('loan_mapping', record.id).then(r => { if (r.data) setAuditHistory(r.data); }).catch(() => {});
      setTimeout(() => setAmendMessage(null), 4000);
    } catch (err: any) {
      setAmendMessage({ text: err?.response?.data?.message || err?.message || 'Failed to amend account number', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAmendGl = async (glId: string, newNumber: string, reason: string) => {
    setSubmitting(true);
    setAmendMessage(null);
    try {
      const res = await loanMappingService.amendGlNumber(record.id, glId, { newNumber, reason });
      const updated = res.data;
      if (updated && onRecordUpdated) {
        onRecordUpdated({
          ...record,
          addonLoanAccountNumber: updated.addonLoanAccountNumber,
          mappedGls: updated.mappedGls,
        } as LoanMappingRecord);
      }
      setAmendingGlId(null);
      setAmendMessage({ text: 'GL Number amended successfully', type: 'success' });
      // Re-fetch audit trail
      auditTrailService.getByRecord('loan_mapping', record.id).then(r => { if (r.data) setAuditHistory(r.data); }).catch(() => {});
      setTimeout(() => setAmendMessage(null), 4000);
    } catch (err: any) {
      setAmendMessage({ text: err?.response?.data?.message || err?.message || 'Failed to amend GL number', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            Loan Mapping
            <span style={getStatusBadgeStyle(status)}>{(status).replace(/_/g, ' ')}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Amend feedback message */}
          {amendMessage && (
            <div style={{
              padding: '0.5rem 0.75rem',
              marginBottom: '0.75rem',
              borderRadius: '0.375rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              background: amendMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: amendMessage.type === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${amendMessage.type === 'success' ? '#86efac' : '#fca5a5'}`,
            }}>
              {amendMessage.text}
            </div>
          )}

          {loading ? (
            <ModalLoadingSkeleton sections={1} fieldsPerRow={2} showTable tableCols={4} tableRows={4} />
          ) : (
            <>
              {/* Mapping Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label className="form-label">Customer</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>{record.customerName} ({record.customerId})</div>
                </div>
                <div>
                  <label className="form-label">Loan Type</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>{(record.addonLoanType && loanTypeLookup[record.addonLoanType]) || record.addonLoanType || '-'}</div>
                </div>
                <div>
                  <label className="form-label">Loan Account Number</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'monospace' }}>{record.addonLoanAccountNumber || '-'}</span>
                    {isAdmin && isAmendable && !amendingAccount && (
                      <button
                        onClick={() => { setAmendingAccount(true); setAmendingGlId(null); setAmendMessage(null); }}
                        title="Amend Loan Account Number"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--primary)', padding: '0.125rem', lineHeight: 1,
                        }}
                      >
                        <IconSystem.edit size={14} />
                      </button>
                    )}
                  </div>
                  {amendingAccount && (
                    <AmendForm
                      currentValue={record.addonLoanAccountNumber || ''}
                      label="Loan Account Number"
                      onConfirm={handleAmendAccount}
                      onCancel={() => setAmendingAccount(false)}
                      submitting={submitting}
                    />
                  )}
                </div>
                <div>
                  <label className="form-label">Approved Amount</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', fontWeight: 700, color: 'var(--primary)' }}>
                    {formatCurrency(record.approvedAmount)}
                  </div>
                </div>
              </div>

              {/* Eligibility Snapshot */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
                background: 'var(--bg-secondary)',
                padding: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Eligible Weight</div>
                  <div style={{ fontWeight: 600 }}>{totalWeight.toFixed(3)} g</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gold Rate</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(record.goldRateAtApproval)}/g</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gross Gold Value</div>
                  <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{formatCurrency(record.grossValueAtApproval)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Eligible Amount</div>
                  <div style={{ fontWeight: 600, color: '#16a34a' }}>{formatCurrency(record.eligibleAmountAtApproval)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding Amount</div>
                  <div style={{ fontWeight: 600, color: '#dc2626' }}>{formatCurrency(record.outstandingAmount)}</div>
                </div>
              </div>

              {/* Mapped GLs with Allocation */}
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
                Mapped Gold Loans & Allocation
              </h3>
              <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                {mappedGls.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No mapped gold loans found</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-tertiary)' }}>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>#</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Number</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Customer</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Eligible Weight (g)</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Original Amount</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Ratio</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Allocation</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappedGls.map((gl, idx) => (
                        <React.Fragment key={gl.id}>
                          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                            <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                                {gl.glNumber}
                                {isAdmin && isAmendable && gl.statusCode === 'OPEN' && amendingGlId !== gl.id && (
                                  <button
                                    onClick={() => { setAmendingGlId(gl.id); setAmendingAccount(false); setAmendMessage(null); }}
                                    title="Amend GL Number"
                                    style={{
                                      background: 'none', border: 'none', cursor: 'pointer',
                                      color: 'var(--primary)', padding: '0.125rem', lineHeight: 1,
                                    }}
                                  >
                                    <IconSystem.edit size={12} />
                                  </button>
                                )}
                              </span>
                            </td>
                            <td style={{ padding: '0.5rem' }}>{gl.glCustomerName}</td>
                            <td style={{ padding: '0.5rem', textAlign: 'left' }}>{(gl.netWeight ?? 0).toFixed(3)}</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(gl.originalLoanAmount)}</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 500 }}>{((gl.allocationRatio ?? 0) * 100).toFixed(2)}%</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }}>{formatCurrency(gl.allocationAmount)}</td>
                            <td style={{ padding: '0.5rem' }}>
                              <span style={getStatusBadgeStyle(gl.statusCode ?? '')}>{(gl.statusCode ?? '').replace(/_/g, ' ')}</span>
                            </td>
                          </tr>
                          {amendingGlId === gl.id && (
                            <tr>
                              <td colSpan={8} style={{ padding: '0 0.5rem 0.5rem' }}>
                                <AmendForm
                                  currentValue={gl.glNumber}
                                  label="GL Number"
                                  onConfirm={(newNum, rsn) => handleAmendGl(gl.id, newNum, rsn)}
                                  onCancel={() => setAmendingGlId(null)}
                                  submitting={submitting}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: 'var(--bg-tertiary)', fontWeight: 600 }}>
                        <td colSpan={3} style={{ padding: '0.5rem' }}>Total</td>
                        <td style={{ padding: '0.5rem', textAlign: 'left' }}>{totalWeight.toFixed(3)}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(mappedGls.reduce((s, gl) => s + (gl.originalLoanAmount ?? 0), 0))}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>100.00%</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--primary)' }}>{formatCurrency(record.approvedAmount)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>

              {/* Approval Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                <div>
                  <label className="form-label">Approved By</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>{record.approvedByName || record.approvedBy}</div>
                </div>
                <div>
                  <label className="form-label">Approved At</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>{formatDate(record.approvedAt)}</div>
                </div>
              </div>

              {/* Change History (Audit Trail) */}
              {auditHistory.length > 0 && (
                <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                  <button
                    onClick={() => setAuditExpanded(!auditExpanded)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
                    }}
                  >
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                      Change History ({auditHistory.length})
                    </h3>
                    <IconSystem.chevronDown
                      size={16}
                      style={{
                        transition: 'transform 0.2s',
                        transform: auditExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </button>
                  {auditExpanded && (
                    <div style={{ marginTop: '0.75rem' }}>
                      {auditHistory.map((entry) => {
                        const fieldLabel = entry.fieldName === 'addon_loan_account_number' ? 'Loan Account Number' : entry.fieldName === 'gl_number' ? 'GL Number' : entry.fieldName;
                        return (
                          <div
                            key={entry.id}
                            style={{
                              padding: '0.75rem',
                              marginBottom: '0.5rem',
                              background: 'rgba(59, 130, 246, 0.04)',
                              borderRadius: '0.375rem',
                              border: '1px solid rgba(59, 130, 246, 0.15)',
                              fontSize: '0.8rem',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                              <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{fieldLabel}</span>
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{formatDate(entry.changedAt)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                              <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>{entry.oldValue || '(empty)'}</span>
                              <span style={{ color: 'var(--text-muted)' }}>&rarr;</span>
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>{entry.newValue}</span>
                            </div>
                            {entry.reason && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                Reason: {entry.reason}
                              </div>
                            )}
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                              By: {entry.changedByName}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {auditLoading && (
                <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Loading change history...
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
